/**
 * NSFieldFreshness
 * ────────────────────────────────────────────────────────────
 * "Last updated X hours ago" line for a template's NetSuite field rules, with a
 * manual re-detect action.
 *
 * NetSuite field detection now runs automatically (on template creation, on
 * vendor confirmation, and nightly when a mapping drifts). That is only
 * trustworthy if it is visible: without this, a mapping refreshed an hour ago
 * and one last touched eight months ago look identical, and nobody can tell
 * whether a missing mandatory field means "NetSuite doesn't require it" or
 * "nothing has looked since March".
 *
 * Props:
 *   templateId     – template whose ns_field_rules to report on
 *   onRefreshed    – optional callback(result) after a successful re-detect
 *   compact        – render as a single inline line (no border/padding)
 */
import React, { useCallback, useEffect, useState } from 'react'
import { RefreshCw, Loader2, Clock, AlertTriangle } from 'lucide-react'
import { nsApi } from '../services/api'
import { nsEntityNouns } from '../utils/nsEntityLabels'

/** Humanise an age in hours: "just now" / "3 hours ago" / "2 days ago". */
export function formatAge(hours) {
  if (hours == null || Number.isNaN(hours)) return null
  if (hours < 1) {
    const mins = Math.max(1, Math.round(hours * 60))
    return mins === 1 ? '1 minute ago' : `${mins} minutes ago`
  }
  if (hours < 24) {
    const h = Math.round(hours)
    return h === 1 ? '1 hour ago' : `${h} hours ago`
  }
  const days = Math.round(hours / 24)
  return days === 1 ? '1 day ago' : `${days} days ago`
}

export default function NSFieldFreshness({ templateId, nsEntityType = 'purchase_order', onRefreshed, compact = false }) {
  // The sample this template's fields were detected from is of its OWN document
  // type — a Vendor Bid template samples bids, not POs.
  const nouns = nsEntityNouns(nsEntityType)
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(() => {
    if (!templateId) return undefined
    let cancelled = false
    setLoading(true)
    nsApi.getFieldRulesStatus(templateId)
      .then(res => { if (!cancelled) { setStatus(res.data || null); setError('') } })
      .catch(() => { if (!cancelled) setError('Could not read field-rule status') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [templateId])

  useEffect(() => load(), [load])

  const handleRefresh = async () => {
    if (!templateId || refreshing) return
    setRefreshing(true)
    setError('')
    try {
      // template_id makes the backend persist what it detects as this
      // template's rules, rather than just reporting them back.
      const res = await nsApi.detectFields({ template_id: templateId, reason: 'manual' })
      load()
      onRefreshed?.(res.data)
    } catch (err) {
      setError(err?.response?.data?.error || 'Field detection failed')
    } finally {
      setRefreshing(false)
    }
  }

  if (!templateId) return null

  const age = formatAge(status?.age_hours)
  // "Never detected" is a real state, not an error: rules seeded before
  // auto-detection existed carry no timestamp at all.
  const never = status?.never_refreshed || !age
  const stale = status?.age_hours != null && status.age_hours > 24

  const body = (
    <div className="flex items-center gap-2 flex-wrap">
      {loading && !status ? (
        <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-400">
          <Loader2 className="h-3 w-3 animate-spin" /> Checking field mapping…
        </span>
      ) : (
        <span
          className={`inline-flex items-center gap-1.5 text-[11px] ${
            stale || never
              ? 'text-amber-600 dark:text-amber-400'
              : 'text-gray-500 dark:text-gray-400'
          }`}
        >
          {stale || never
            ? <AlertTriangle className="h-3 w-3 flex-shrink-0" />
            : <Clock className="h-3 w-3 flex-shrink-0" />}
          {never
            ? 'NetSuite fields never auto-detected'
            : <>Fields last updated <strong className="font-semibold">{age}</strong></>}
          {status?.detection_sample_size ? (
            <span className="text-gray-400">
              · from {status.detection_sample_size} {nouns.abbrevPlural}
            </span>
          ) : null}
          {status?.manual_count ? (
            <span className="text-gray-400">
              · {status.manual_count} set manually
            </span>
          ) : null}
        </span>
      )}

      <button
        type="button"
        onClick={handleRefresh}
        disabled={refreshing}
        className="inline-flex items-center gap-1 text-[11px] font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 disabled:opacity-50"
      >
        {refreshing
          ? <Loader2 className="h-3 w-3 animate-spin" />
          : <RefreshCw className="h-3 w-3" />}
        {refreshing ? 'Detecting…' : 'Re-detect now'}
      </button>

      {error && (
        <span className="text-[11px] text-red-500 dark:text-red-400">{error}</span>
      )}
    </div>
  )

  if (compact) return body

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/40 px-3 py-2">
      {body}
    </div>
  )
}
